'use client';

import React, { useMemo } from 'react';
import { ExperienceType } from '../types';
import { typography } from '@/design-system/tokens/typography';
import Border from '../../common/border';

interface ExperienceExclusionsProps {
  experience: unknown;
  type: ExperienceType;
}

export const ExperienceExclusions: React.FC<ExperienceExclusionsProps> = ({
  experience,
  type,
}) => {
  const exclusions = useMemo(() => {
    if (!experience) return [];
    const exp = experience as {
      exclusions?: Array<
        | string
        | {
            title?: string;
            name?: string;
            description?: string;
            [key: string]: unknown;
          }
      >;
      excluded?: Array<
        | string
        | {
            title?: string;
            name?: string;
            description?: string;
            [key: string]: unknown;
          }
      >;
      activityExcludes?: Array<
        | string
        | {
            title?: string;
            name?: string;
            description?: string;
            [key: string]: unknown;
          }
      >;
    };
    // For activities and stays, check activityExcludes first (API field)
    if (type === 'activity' || type === 'stay') {
      return exp?.activityExcludes || exp?.exclusions || exp?.excluded || [];
    }
    return exp?.exclusions || exp?.excluded || [];
  }, [experience, type]);

  const hasExclusions = Array.isArray(exclusions) && exclusions.length > 0;
  const isActivity = type === 'activity';
  if (!experience) return null;
  // For activity, always show the section (even when empty). For stay/package, hide when no data.
  if (!hasExclusions && !isActivity) return null;

  // Helper to extract string from exclusion (handles both string and object)
  const getExclusionText = (
    exclusion:
      | string
      | {
          title?: string;
          name?: string;
          description?: string;
          [key: string]: unknown;
        }
  ): string => {
    if (typeof exclusion === 'string') {
      return exclusion;
    }
    if (typeof exclusion === 'object' && exclusion !== null) {
      return (exclusion.title ||
        exclusion.name ||
        exclusion.description ||
        '') as string;
    }
    return '';
  };

  return (
    <div className='space-y-4'>
      <h2
        className='font-semibold text-gray-900'
        style={{
          fontSize: typography.size.lg, // 18px
          fontFamily: typography.fonts.primary,
          lineHeight: 'normal',
        }}
      >
        What&apos;s Not Included
      </h2>
      <div className='space-y-2'>
        {hasExclusions ? (
          exclusions.map((exclusion, index) => {
            const exclusionText = getExclusionText(exclusion);
            if (!exclusionText) return null;
            return (
              <div key={index} className='flex items-start gap-3'>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                  className='text-red-600 mt-0.5 flex-shrink-0'
                >
                  <path
                    d='M15 5L5 15M5 5l10 10'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <p className='text-gray-700'>{exclusionText}</p>
              </div>
            );
          })
        ) : (
          <p className='text-gray-500'>
            No exclusions listed for this activity.
          </p>
        )}
      </div>
      <div className='pt-4'>
        <Border />
      </div>
    </div>
  );
};
